<script setup lang="ts">
/*
 * Flat ink side sheet for the mobile nav (left) and the mini cart (right):
 * dim backdrop, sliding panel, dialog semantics and the shared modal
 * plumbing (focus trap, Escape, scroll lock, focus restore). Content and its
 * own header come through the slot; the slot gets `close` for the X button.
 * Uses a CSS transition rather than `animate-slide-in` so it also leaves
 * smoothly and can come from the left.
 */
import { ref, toRef } from 'vue';
import { useModalFocus } from '../../composables/useModalFocus';

const props = defineProps<{ open: boolean; side: 'left' | 'right'; label: string; widthClass: string }>();
const emit = defineEmits<{ close: [] }>();

const panel = ref<HTMLElement | null>(null);
useModalFocus(panel, toRef(props, 'open'), () => emit('close'));

const FROM = { left: '-translate-x-full', right: 'translate-x-full' } as const;
</script>

<template>
  <div class="fixed inset-0 z-[70]" :class="{ 'pointer-events-none': !open }">
    <Transition
      enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
      leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div v-if="open" class="absolute inset-0 bg-black/70" aria-hidden="true" @click="emit('close')"></div>
    </Transition>
    <Transition
      enter-active-class="transition-transform duration-300 ease-out motion-reduce:transition-none"
      leave-active-class="transition-transform duration-200 ease-in motion-reduce:transition-none"
      :enter-from-class="FROM[side]"
      :leave-to-class="FROM[side]"
    >
      <aside
        v-if="open"
        ref="panel"
        role="dialog"
        aria-modal="true"
        :aria-label="label"
        tabindex="-1"
        class="pointer-events-auto absolute inset-y-0 flex max-w-[92vw] flex-col border-border bg-bg-elev outline-none"
        :class="[widthClass, side === 'left' ? 'left-0 border-r' : 'right-0 border-l']"
      >
        <slot :close="() => emit('close')" />
      </aside>
    </Transition>
  </div>
</template>
